import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PortalLayout from '../../components/Portal/PortalLayout';
import { memberAPI } from '../../services/api';
import { getSlotDescription } from '../../components/Portal/TimeSlotPicker';

/**
 * PortalDashboard - Member portal home/hub page
 * Shows member summary and quick links to edit sections
 */
const PortalDashboard = () => {
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Get member ID from stored user
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const memberId = user.memberId;

  useEffect(() => {
    const fetchMemberData = async () => {
      if (!memberId) {
        setError('No member ID found. Please log in again.');
        setLoading(false);
        return;
      }

      try {
        const response = await memberAPI.getPortalSummary(memberId);
        setMember(response.data.data.member);
      } catch (err) {
        console.error('Failed to load member data:', err);
        setError('Failed to load your profile. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchMemberData();
  }, [memberId]);

  if (loading) {
    return (
      <PortalLayout title="Home">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-green-500 border-t-transparent rounded-full"></div>
        </div>
      </PortalLayout>
    );
  }

  if (error) {
    return (
      <PortalLayout title="Home">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      </PortalLayout>
    );
  }

  const sections = [
    {
      to: '/portal/profile',
      title: 'Profile',
      description: 'Name, email, phone, address',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      preview: member?.email || 'No email set',
    },
    {
      to: '/portal/preferences',
      title: 'Tee Time Preferences',
      description: 'Time slots, partners, ride/walk',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      preview: getSlotDescription(member?.defaultPreferences?.timeSlotPreference || 'no-preference'),
    },
    {
      to: '/portal/communication',
      title: 'Communication',
      description: 'Email & SMS notification settings',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      ),
      preview: member?.communicationPreferences?.emailNotifications ? 'Emails enabled' : 'Emails disabled',
    },
    {
      to: '/portal/last-minute',
      title: 'Last-Minute Invites',
      description: 'Get notified for open spots',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      preview: member?.lastMinuteInvitePreferences?.acceptInvites ? 'Opted in' : 'Opted out',
    },
  ];

  return (
    <PortalLayout title="Home" member={member}>
      {/* Welcome Card */}
      <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-xl p-6 text-white mb-6 shadow-lg">
        <h2 className="text-2xl font-bold mb-1">
          Welcome, {member?.firstName || 'Member'}!
        </h2>
        <p className="text-green-100 text-sm">
          Manage your golf preferences and settings
        </p>

        <div className="mt-4 grid grid-cols-2 gap-4">
          <div className="bg-white/20 rounded-lg p-3">
            <p className="text-green-100 text-xs uppercase tracking-wide">Handicap</p>
            <p className="text-2xl font-bold">{member?.handicapIndex?.toFixed(1) || '--'}</p>
          </div>
          <div className="bg-white/20 rounded-lg p-3">
            <p className="text-green-100 text-xs uppercase tracking-wide">GHIN</p>
            <p className="text-lg font-bold">{member?.ghin || '--'}</p>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="space-y-3">
        <h3 className="font-medium text-gray-700">Settings</h3>

        {sections.map((section) => (
          <Link
            key={section.to}
            to={section.to}
            className="block bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:border-green-300 hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 text-green-600">
                {section.icon}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-900">{section.title}</h4>
                <p className="text-sm text-gray-500 truncate">{section.description}</p>
                <p className="text-sm text-green-600 mt-1 truncate">{section.preview}</p>
              </div>
              <div className="flex-shrink-0 text-gray-400">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Member Since */}
      {member?.memberSince && (
        <div className="mt-6 text-center text-sm text-gray-500">
          Member since {new Date(member.memberSince).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </div>
      )}
    </PortalLayout>
  );
};

export default PortalDashboard;
